import {
  ROOM_TYPE_FILTER,
  ROOM_TYPE_SPECIFIC,
  SHARING_OPTIONS_BATHROOM,
  SHARING_OPTIONS_KITCHEN,
} from "@Pages/Search/components/FilterSection/data/filtersList";
import { formatAmenityName } from "@Pages/Search/components/FilterSection/components/Filters/AmenitiesFilter/utils/formatAmenityName";
import { formatBillName } from "@Pages/Search/components/FilterSection/components/Filters/BillsIncludedFilter/utils/formatBillName";
import {
  AppliedFilterSequenceItem,
  AppliedFilterSource,
} from "@Pages/Search/utils/appliedFilterSequence";

export type AppliedFilterPill = {
  source: AppliedFilterSource;
  value: string;
  label: string;
  iconKey: string;
};

const ROOM_TYPE_OPTIONS = [
  ...ROOM_TYPE_SPECIFIC.list,
  ...ROOM_TYPE_FILTER.list,
  ...SHARING_OPTIONS_BATHROOM.list,
  ...SHARING_OPTIONS_KITCHEN.list,
];

export const normalizeFilterTagKey = (value?: string): string =>
  (value || "").toLowerCase().replace(/[^a-z0-9]/g, "");

const findMatchingTag = (
  value: string,
  uniqueTags: string[] = [],
  uniqueTagsEn: string[] = [],
): { display: string; en: string } | null => {
  const key = normalizeFilterTagKey(value);
  if (!key) return null;
  const lookup = uniqueTagsEn?.length ? uniqueTagsEn : uniqueTags;
  const index = lookup.findIndex((tag) => {
    const tagKey = normalizeFilterTagKey(tag);
    return Boolean(tagKey) && (tagKey === key || tagKey.includes(key) || key.includes(tagKey));
  });
  if (index === -1) return null;
  return {
    display: uniqueTags[index] || lookup[index],
    en: uniqueTagsEn[index] || uniqueTags[index] || lookup[index],
  };
};

const getRoomTypeLabel = (value: string): string => {
  const option = ROOM_TYPE_OPTIONS.find((item) => item.value === value);
  return option?.name || formatAmenityName(value);
};

export const getAppliedFilterPills = ({
  sequence,
  uniqueTags = [],
  uniqueTagsEn = [],
  activeRoomTypes,
}: {
  sequence: AppliedFilterSequenceItem[];
  uniqueTags?: string[];
  uniqueTagsEn?: string[];
  activeRoomTypes?: string[];
}): AppliedFilterPill[] => {
  const offeredRoomTypes = new Set(
    (activeRoomTypes || []).map((type) => String(type).toLowerCase()),
  );

  return sequence
    .map((item) => {
      if (item.source === "roomType") {
        // Exact match only. Composite filters (ensuite, studio, 1b) are stored on
        // child meta.unit_type and returned in active_room_types by the API —
        // do not infer ensuite from private_bathroom (studios also have that).
        if (!offeredRoomTypes.has(item.value.toLowerCase())) return null;
        return {
          source: item.source,
          value: item.value,
          label: getRoomTypeLabel(item.value),
          iconKey: item.value,
        };
      }

      const matched = findMatchingTag(item.value, uniqueTags, uniqueTagsEn);
      const formatted =
        item.source === "billsIncluded"
          ? formatBillName(item.value)
          : formatAmenityName(item.value);
      return {
        source: item.source,
        value: item.value,
        label: matched?.display || formatted,
        iconKey: matched?.en || item.value.replace(/_/g, " "),
      };
    })
    .filter((pill): pill is AppliedFilterPill => Boolean(pill));
};

export const getAppliedFilterExcludeKeys = (pills: AppliedFilterPill[]): Set<string> =>
  new Set(
    pills
      .filter((pill) => pill.source !== "roomType")
      .map((pill) => normalizeFilterTagKey(pill.label))
      .concat(
        pills
          .filter((pill) => pill.source !== "roomType")
          .map((pill) => normalizeFilterTagKey(pill.value)),
      ),
  );
